/**
 * 63.4 step 5 (ARCHITECTURE §5.13 "63.3 and 63.4, the design"; lane F): what each kind of blow does to each material,
 * as a share of its damage, and what props are made of. Every blow goes through the damage door (core/damage.ts),
 * which multiplies by this table — so a change to what breaks is a change to these numbers, not to the code.
 *
 * **Step 5's numbers are today's, exactly:** a blast hurts people, structures, hulls and terrain features; a bullet,
 * melee and fire hurt people only (a bullet stops at cover and plinks off a hull, SCALE.md §6); a hazard hurts people
 * and structures (the crystal's shatter); the ground dropping hurts what stood on it; a landing hurts people. A burst
 * (a rocket, a grenade) is a blast on everything but wood.
 *
 * **Step 6, decision 130 (a): trees.** A tree has `PROPS.flora.health`, and a blast takes it whole within its own
 * radius (no falloff: today's flora rule's reach, measured to the trunk — core/propDamage.ts). A shell's blow on wood
 * is twenty times its damage, so any explosive shell (damage 5 or more) fells a tree in a hit; a burst's is 0.4 of its
 * damage, so a rocket (90) takes 36 of a tree's 100 — three to fell one. (b) Rocks stand, for now: stone takes nothing.
 */
import type { BlowCause, Material } from '../types/body';
import type { StructureType } from '../types/world';

/**
 * 63.1d's follow-up (lane B's ride trace: every 6×6 destroyed between 38 and 60 s in every seed by one emplacement shell):
 * a shell hurts a structure linearly from its shape out to its damage circle, 2.2 × its blast radius — planet-family,
 * ~80 people for a standard shell at a person's scale — so any standard shell within ~23 people of a person-scale truck
 * (20 health) destroyed it, a heavy one within ~75. A moving vehicle's hull (the transport, the carrier, a bike; not the
 * gun pit, fixed, whose circle is the guns' duel) feels a shell's blast over `reachShare` of its blast
 * radius instead (a standard shell's ~6 people, a heavy's ~10), linear from the hull's shape as before: a direct hit
 * kills a 6×6 (a standard within ~1.8 people of the hull, a heavy within ~6), a near miss leaves it damaged (63.1e's
 * damaged form), a shell further off does nothing to it. A burst (a rocket's, a grenade's) keeps its own reach.
 */
export const VEHICLE_BLAST = { reachShare: 0.17 };

/**
 * 63.1o: `soft` — an unarmoured vehicle's (the technical's): a blast and a burst as a hull takes them, and a rifle round at
 * `bullet`'s share (the coordinator's reading of a technical — "small arms wear it" — not the owner's words: his to veto in play).
 */
export const DAMAGE_VS: Record<BlowCause, Record<Material, number>> = {
  blast: { flesh: 1, light: 1, hull: 1, earth: 1, wood: 20, stone: 0, soft: 1, panel: 1, concrete: 1 },
  burst: { flesh: 1, light: 1, hull: 1, earth: 1, wood: 0.4, stone: 0, soft: 1, panel: 1, concrete: 0.3 },
  bullet: { flesh: 1, light: 0, hull: 0, earth: 0, wood: 0, stone: 0, soft: 0.15, panel: 0.1, concrete: 0 },
  melee: { flesh: 1, light: 0, hull: 0, earth: 0, wood: 0, stone: 0, soft: 0, panel: 0, concrete: 0 },
  landing: { flesh: 1, light: 0, hull: 0, earth: 0, wood: 0, stone: 0, soft: 0, panel: 0, concrete: 0 },
  hazard: { flesh: 1, light: 1, hull: 1, earth: 0, wood: 0, stone: 0, soft: 1, panel: 1, concrete: 0 },
  fire: { flesh: 1, light: 0, hull: 0, earth: 0, wood: 0, stone: 0, soft: 0, panel: 0, concrete: 0 },
  drop: { flesh: 1, light: 1, hull: 1, earth: 1, wood: 0, stone: 0, soft: 1, panel: 1, concrete: 0 },
  flak: { flesh: 0.6, light: 1, hull: 1, earth: 0, wood: 0.2, stone: 0, soft: 1, panel: 1, concrete: 0 },   // 69.9: a CIWS / C-RAM burst — a cannon's rounds that wear a ship's hull and parts
  impact: { flesh: 1, light: 0, hull: 0, earth: 0, wood: 0, stone: 0, soft: 0, panel: 0, concrete: 0 },
  pierce: { flesh: 0, light: 1, hull: 1, earth: 0.3, wood: 1, stone: 0, soft: 1, panel: 1, concrete: 0.5 },   // 72.3 (DECISIONS 335): an AP round's or a jet's line through modules (core/moduleDamage.ts pierceModules); it meets modules only   // 63.19: a vehicle's blow on a person (blunt: the vest takes nothing, armoredAmount)
};

/**
 * 63.4's future, partial destruction step 2 (ARCHITECTURE §5.13): a form a thing takes as its health falls — at or
 * under `at` of its full health — in order, each deeper than the one before (forms only advance). A tree's are lane E's
 * damaged model at `level` (rendering/treeForms.ts), measured into constants/treeFormShapes.ts. Read by nothing in
 * play until step 3, behind the `partialDestruction` switch.
 */
export interface PropForm { name: string; at: number; level: number }

/**
 * Partial destruction step 3: the pieces a tree's forms shed. A limb lost at a form step (a piece of its wood shortened
 * by more than `minLength` people) falls by a keyed closed-form rule — out from the trunk and along the blow, a share of
 * its height (`reach`), turned a keyed way — and lies on the ground. Landing on someone is a light `drop` blow (decision
 * 141): `hitPerVolume` damage a person³ of wood (a limb half a person long and a sixteenth thick, ~0.0014 person³: ~7), at most
 * `hitMax`, never a kill. Past `cap` pieces a planet, the oldest
 * settles into the nearest heap (a mound of `heapAspect` height to radius); a heap stays a heap.
 */
export const DEBRIS = { cap: 48, minLength: 0.05, reach: [0.2, 0.8] as const, hitPerVolume: 5000, hitMax: 25, heapAspect: 0.5 };   // a kit tree's limb (~0.5 person long, 0.06 thick): ~7

/**
 * What the props are made of, their health (null: cannot be destroyed — rocks, 130 (b)) and the forms they step through
 * before they fall (partial destruction step 2; a tree: limbs lost at two thirds, its crown gone at a third). Step 8
 * draws their destroyed forms.
 */
export const PROPS: Record<'flora' | 'rock', { material: Material; health: number | null; forms?: readonly PropForm[] }> = {
  flora: { material: 'wood', health: 100, forms: [{ name: 'limbsLost', at: 2 / 3, level: 0.5 }, { name: 'crownLost', at: 1 / 3, level: 1 }] },
  rock: { material: 'stone', health: null },
};

/** Partial destruction step 4: what a marked part does — its job (`function`: the gun fires, the tower sees) or the cover it gives. */
export type PartRole = 'function' | 'cover';
/**
 * A marked part: its index in the body's hit shape (constants/hitboxes.ts), its role, its share of the body's health, and
 * (step 5) whether it falls away at 0 — debris on the trees' rules (core/debris.ts shedParts).
 */
export interface MarkedPart { part: number; role: PartRole; share: number; detaches?: boolean }

/**
 * Partial destruction step 4 (ARCHITECTURE §5.13, 63.4's future, "parts that act"; decision 141): the parts that act on
 * their own, by structure type, with `partialDestruction` on. Each keeps its own health (its `share` of the body's, from
 * whole) and takes its share of a blast by its own distance; at 0 it is gone — its shape too — and its role stops: the emplacement's
 * breech is its gun (worn, it fires wider and slower; gone, silent — 141), its shield the cover; the tower's radar head
 * is its sight (gone: blind). The body keeps its one number as before. The HMG's gun is not a target (its ring is), so its
 * wear is the body's health. Unplayed proposals (decision 121).
 */
export const STRUCTURE_PARTS: Readonly<Partial<Record<StructureType, readonly MarkedPart[]>>> = {
  emplacement: [{ part: 2, role: 'function', share: 0.3 }, { part: 3, role: 'cover', share: 0.25, detaches: true }],   // EMPLACEMENT_PARTS: plinth, carriage, breech, shield, crate
  tower: [{ part: 1, role: 'function', share: 0.3, detaches: true }],   // the platform and mast, the radar head
  pointDefence: [{ part: 1, role: 'function', share: 0.25, detaches: true }, { part: 2, role: 'function', share: 0.3 }],   // 69.9: the ring, the radar head (gone: blind), the gun (worn: slower and less sure; gone: silent)
};

/**
 * Decision 141 ("less accurate"): a worn gun at wear w (0 whole … 1 its part gone) — a shell gun's bearing and elevation
 * thrown off by up to `shellSpread` × w radians each, its reload × (1 + `slow` × w); a mounted gun's spread × (1 +
 * `bulletSpread` × w) and its interval × (1 + `slow` × w). Silent only when its part is gone (or the body destroyed).
 */
export const WORN_GUN = { shellSpread: 0.06, bulletSpread: 2, slow: 1 };
